import { useState } from 'react';
import {
  LayoutDashboard,
  UtensilsCrossed,
  Package,
  Users,
  Calendar,
  Contact,
  MessageSquare,
  Settings,
  Search,
  BookOpen,
  HelpCircle,
} from 'lucide-react';
import { guides, getGuidesByCategory, FeatureGuide } from '../data/guides';
import HelpPanel from '../components/HelpPanel';

const iconMap: Record<string, typeof LayoutDashboard> = {
  LayoutDashboard,
  UtensilsCrossed,
  Package,
  Users,
  Calendar,
  Contact,
  MessageSquare,
  Settings,
};

const categoryLabels: Record<string, string> = {
  operations: 'Operations',
  customers: 'Customers',
  team: 'Team',
  tools: 'Tools & Settings',
};

const categoryIcons: Record<string, typeof LayoutDashboard> = {
  operations: LayoutDashboard,
  customers: Contact,
  team: Users,
  tools: Settings,
};

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuide, setSelectedGuide] = useState<FeatureGuide | null>(null);

  const categories = getGuidesByCategory();

  const filteredGuides = searchQuery
    ? guides.filter(
        (guide) =>
          guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          guide.canDo.items.some((item) =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : null;

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || HelpCircle;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Help & Guides</h1>
          <p className="text-slate-500">
            Learn what each feature can do and how to use it effectively
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-lg">
          <BookOpen className="w-5 h-5 text-primary-600" />
          <span className="text-sm font-medium text-primary-700">
            {guides.length} Feature Guides
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search guides..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {/* Search Results */}
      {filteredGuides && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Search Results ({filteredGuides.length})
          </h2>
          {filteredGuides.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No guides found for "{searchQuery}"</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGuides.map((guide) => {
                const Icon = getIcon(guide.icon);
                return (
                  <button
                    key={guide.id}
                    onClick={() => setSelectedGuide(guide)}
                    className="card p-4 text-left hover:shadow-md hover:border-primary-200 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                        <Icon className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                          {guide.title}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                          {guide.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Categorized Guides */}
      {!filteredGuides && (
        <div className="space-y-8">
          {Object.entries(categories).map(([category, categoryGuides]) => {
            const CategoryIcon = categoryIcons[category];
            return (
              <section key={category}>
                <div className="flex items-center gap-2 mb-4">
                  <CategoryIcon className="w-5 h-5 text-slate-600" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    {categoryLabels[category]}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryGuides.map((guide) => {
                    const Icon = getIcon(guide.icon);
                    return (
                      <button
                        key={guide.id}
                        onClick={() => setSelectedGuide(guide)}
                        className="card p-4 text-left hover:shadow-md hover:border-primary-200 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                            <Icon className="w-5 h-5 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                              {guide.title}
                            </h3>
                            <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                              {guide.description}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                          <span>{guide.canDo.items.length} capabilities</span>
                          <span>{guide.howTo.length} tutorials</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Quick Tips */}
      <section className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl">
        <h2 className="text-lg font-semibold text-primary-900 mb-3">
          Quick Tips
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/80 rounded-lg p-4">
            <p className="font-medium text-slate-800 mb-1">
              Look for the help icon
            </p>
            <p className="text-sm text-slate-600">
              On each page, click the{' '}
              <HelpCircle className="w-4 h-4 inline text-primary-600" /> icon in
              the header for page-specific help.
            </p>
          </div>
          <div className="bg-white/80 rounded-lg p-4">
            <p className="font-medium text-slate-800 mb-1">
              Use the AI assistant
            </p>
            <p className="text-sm text-slate-600">
              Click the chat bubble in the bottom right to ask questions or
              perform tasks with natural language.
            </p>
          </div>
          <div className="bg-white/80 rounded-lg p-4">
            <p className="font-medium text-slate-800 mb-1">
              Keyboard shortcuts
            </p>
            <p className="text-sm text-slate-600">
              Press <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">?</kbd> anywhere to see available shortcuts (coming soon).
            </p>
          </div>
          <div className="bg-white/80 rounded-lg p-4">
            <p className="font-medium text-slate-800 mb-1">Need more help?</p>
            <p className="text-sm text-slate-600">
              Contact support at{' '}
              <a
                href="mailto:leah@mystep.ai"
                className="text-primary-600 hover:underline"
              >
                leah@mystep.ai
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Help Panel */}
      {selectedGuide && (
        <HelpPanel
          guide={selectedGuide}
          onClose={() => setSelectedGuide(null)}
        />
      )}
    </div>
  );
}
